import { useState } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { formatINR } from "@/lib/brand";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [addr, setAddr] = useState({ full_name: user?.name || "", phone: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "IN" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (items.length === 0) { toast.error("Bag is empty"); return; }
    if (!addr.full_name || !addr.line1 || !addr.city || !addr.postal_code || !addr.phone) {
      toast.error("Please complete delivery details"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map(i => ({ kind: i.kind, gift_amount: i.gift_amount, quantity: i.quantity })),
        address: addr, origin_url: window.location.origin,
      });
      clear();
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed"); setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto text-center pt-40 pb-24 px-8">
        <h1 className="font-editorial text-5xl">The bag is empty.</h1>
        <div className="rule-gold w-16 mx-auto my-8" />
        <Link to="/gifts" className="tracking-widest-plus text-[11px] uppercase link-draw">Explore gift cards →</Link>
      </div>
    );
  }

  return (
    <div className="bg-ivory text-ink pt-32 pb-24">
      <div className="max-w-[1200px] mx-auto px-8 md:px-16 grid md:grid-cols-5 gap-16">
        <div className="md:col-span-3">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Checkout</p>
          <h1 className="font-editorial text-5xl md:text-6xl tracking-tight">Delivery details</h1>
          <div className="rule-gold w-16 my-8" />
          <div className="grid grid-cols-2 gap-6">
            {[["full_name","Full name","col-span-2"],["phone","Phone","col-span-2"],["line1","Address","col-span-2"],["line2","Apartment (optional)","col-span-2"],["city","City",""],["state","State",""],["postal_code","Postal code",""],["country","Country",""]].map(([k,l,cls]) => (
              <label key={k} className={`block ${cls}`}>
                <span className="text-[10px] tracking-widest-plus uppercase text-black/50">{l}</span>
                <input data-testid={`addr-${k}`} value={addr[k]} onChange={(e)=>setAddr({...addr,[k]:e.target.value})}
                  className="w-full border-b border-black/25 focus:border-gold py-2 outline-none bg-transparent" />
              </label>
            ))}
          </div>
        </div>
        <aside className="md:col-span-2 bg-onyx text-ivory p-10 h-max sticky top-28">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Your bag</p>
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/brand/looxs-wordmark.png`} alt="LOOXS" style={{ height: 40, width: "auto", display: "block" }} draggable="false" />
          <div className="rule-gold w-12 my-6" />
          <ul className="space-y-4 mb-6">
            {items.map((i, idx) => {
              const key = i.sku ?? i.slug ?? (i.kind === "gift_card" ? `gift-${i.gift_amount}` : `row-${idx}`);
              return (
              <li key={key} className="flex justify-between text-sm">
                <div>
                  <p>{i.name}</p>
                  <p className="text-[10px] tracking-widest-plus uppercase text-ivory/50">x{i.quantity}</p>
                </div>
                <span>{formatINR(i.price * i.quantity)}</span>
              </li>
            );})}
          </ul>
          <div className="border-t border-ivory/15 pt-4 flex justify-between text-lg font-editorial">
            <span>Total</span><span data-testid="checkout-total">{formatINR(subtotal)}</span>
          </div>
          <button data-testid="pay-btn" onClick={submit} disabled={busy}
            className="mt-8 w-full bg-gold text-ink py-4 text-[11px] tracking-widest-plus uppercase hover:bg-ivory transition-colors duration-500 disabled:opacity-40">
            {busy ? "Redirecting…" : "Pay securely"}
          </button>
          <p className="mt-4 text-[10px] tracking-widest-plus uppercase text-ivory/50 text-center">Stripe secure checkout</p>
        </aside>
      </div>
    </div>
  );
}
